'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPencil, faFolderBookmark } from '@fortawesome/pro-solid-svg-icons';
import { faFolderOpen } from '@fortawesome/pro-solid-svg-icons';
import { Typography, Button, Checkbox } from 'src/components';

interface TeamFoldersSectionProps {
  accountFoldersData: any;
  isEditingFolders: boolean;
  isUpdating: boolean;
  selectedFolders: string[];
  hasFolderChanges: boolean;
  setIsEditingFolders: (isEditing: boolean) => void;
  handleFolderToggle: (folderId: string) => void;
  handleCancelFolderChanges: () => void;
  handleSaveFolderAssignments: () => void;
}

const TeamFoldersSection: React.FC<TeamFoldersSectionProps> = ({
  accountFoldersData,
  isEditingFolders,
  isUpdating,
  selectedFolders,
  hasFolderChanges,
  setIsEditingFolders,
  handleFolderToggle,
  handleCancelFolderChanges,
  handleSaveFolderAssignments,
}) => {
  const [expandedFolderId, setExpandedFolderId] = useState<string | null>(null);

  const toggleFolder = (folderId: string) => {
    setExpandedFolderId(expandedFolderId === folderId ? null : folderId);
  };

  return (
    <div className="flex-1 border border-neutral-200 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <Typography variant="h5" className="text-neutral-700">
          Folder Assignments
        </Typography>
        <div className="flex items-center">
          <Typography className="text-xs font-medium text-neutral-700 bg-neutral-100 px-2 py-1 rounded-full mr-4">
            {selectedFolders.length} folder
            {selectedFolders.length === 1 ? '' : 's'}
          </Typography>
          {!isEditingFolders ? (
            <Button
              variant="solid"
              color="neutral"
              size="sm"
              startDecorator={<Icon icon={faPencil} />}
              onClick={() => setIsEditingFolders(true)}
              data-testid="edit-team-folders-button"
            >
              Edit Folders
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outlined"
                color="neutral"
                size="sm"
                onClick={handleCancelFolderChanges}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                size="sm"
                onClick={handleSaveFolderAssignments}
                disabled={isUpdating || !hasFolderChanges}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="">
        {isEditingFolders ? (
          <div className="grid grid-cols-1 gap-4">
            {accountFoldersData?.results?.length > 0 ? (
              accountFoldersData.results.map((folder: any) => (
                <div
                  key={folder.id}
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Checkbox
                    id={`folder-${folder.id}`}
                    data-testid={`folder-checkbox-${folder.id}`}
                    checked={selectedFolders.includes(folder.id.toString())}
                    onChange={() => handleFolderToggle(folder.id.toString())}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon
                        icon={faFolderBookmark}
                        className="text-neutral-500"
                      />
                      <Typography className="font-semibold text-gray-900">
                        {folder.label}
                      </Typography>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex justify-center p-4">
                <Typography className="text-neutral-500">
                  No folders available.
                </Typography>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {selectedFolders.length > 0 ? (
              accountFoldersData?.results
                ?.filter((folder: any) =>
                  selectedFolders.includes(folder.id.toString())
                )
                .map((folder: any) => (
                  <div key={folder.id} className="flex flex-col">
                    <div
                      className="flex flex-col gap-3 p-4 border border-neutral-200 rounded-lg cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleFolder(folder.id.toString())}
                      data-testid={`folder-${folder.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon
                          icon={
                            expandedFolderId === folder.id.toString()
                              ? faFolderOpen
                              : faFolderBookmark
                          }
                          className="text-neutral-500"
                        />
                        <div className="flex flex-col flex-1">
                          <Typography className="font-medium text-neutral-800">
                            {folder.label}
                          </Typography>
                        </div>
                      </div>
                      {expandedFolderId === folder.id.toString() && (
                        <div className="pl-6">
                          {(folder.provider_cohorts &&
                            folder.provider_cohorts.length > 0) ||
                          (folder.filter_sets &&
                            folder.filter_sets.length > 0) ? (
                            <div className="space-y-4">
                              {/* Provider Cohorts Section */}
                              {folder.provider_cohorts &&
                                folder.provider_cohorts.length > 0 && (
                                  <div className="space-y-2">
                                    {folder.provider_cohorts.map((cohort) => (
                                      <div
                                        key={cohort.id}
                                        className="flex flex-col gap-1 p-2 rounded-md hover:bg-gray-50"
                                      >
                                        <Typography className="text-sm font-semibold text-primary">
                                          {cohort.label}
                                        </Typography>
                                        <Typography className="text-sm text-tertiary">
                                          Encounters
                                        </Typography>
                                      </div>
                                    ))}
                                  </div>
                                )}

                              {/* Filter Sets Section */}
                              {folder.filter_sets &&
                                folder.filter_sets.length > 0 && (
                                  <div className="pl-2">
                                    {folder.filter_sets.map((filterSet) => (
                                      <div
                                        key={filterSet.id}
                                        className="flex flex-col gap-1 rounded-md hover:bg-gray-50"
                                      >
                                        <Typography className="text-sm font-semibold text-primary">
                                          {filterSet.label}
                                        </Typography>
                                        <Typography className="text-sm text-tertiary">
                                          All Claims
                                        </Typography>
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          ) : (
                            <Typography className="text-sm text-neutral-500 py-2">
                              No filter sets in this folder.
                            </Typography>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
            ) : (
              <div className="flex justify-center p-4 border border-neutral-200 rounded-lg">
                <Typography className="text-neutral-500">
                  No folders assigned to this team.
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamFoldersSection;
